import { createContext, useContext } from "react";
import { createStore, type StoreApi, useStore } from "zustand";
import { persist } from "zustand/middleware";

import { cleanRange, labelsOf } from "@app/components/dashboard-pathway/conditions";
import type {
  PathwayConditions,
  PathwayRange,
  Representation,
} from "@app/types/dashboard-pathway";

/**
 * The exploration dashboard's arrangement — which cards exist, what each asks, and what is selected.
 *
 * Persisted because a card is meant to be a permanent object: "Geste × Niveaux drawn as a ruler",
 * pinned today, is still a ruler tomorrow morning. It is also what makes a pinned QUESTION possible,
 * since a question here is nothing but a saved card configuration.
 *
 * The conditions live here too: the export and cohort creation read the board's selection from
 * here, so they never disagree with what is on screen.
 *
 * The ARRANGEMENT — which cards, in what order, and which questions are pinned — additionally lives
 * on the server, and that is the difference between a preference and a property of a browser. A
 * doctor who arranges their board in the consultation room must find it arranged on the ward, and
 * `localStorage` cannot do that: it loses the layout on a new machine, a cleared cache or a second
 * screen. The local copy is kept as an immediate-paint cache and the server answer overwrites it on
 * load — the same shape `UserFieldVisibility` established, with one difference: a layout is small
 * and self-contained, so it is stored whole rather than as deltas.
 */

export type CardKind = "composition" | "singularite";

export interface PathwayCard {
  id: string;
  kind: CardKind;
  /** Ontology / native dimension keys, in order. The LAST one segments; the others nest. */
  dims: string[];
  /** Counts or proportions. Same data, two questions. */
  mode: "n" | "pct";
  /** null = whatever the server says is the default for these dimension types. */
  representation: Representation | null;
  /** Set only when the reader renamed it; otherwise the title is derived from the dimensions. */
  title?: string;
  /** Singularity cards only: which denominator to rank. */
  family?: "acte" | "patient" | null;
}

/** Whether two cards ask the same question — and so draw the same thing. A title is only a name. */
export function sameQuestion(a: Omit<PathwayCard, "id">, b: Omit<PathwayCard, "id">): boolean {
  return (
    a.kind === b.kind &&
    a.mode === b.mode &&
    a.representation === b.representation &&
    (a.family ?? null) === (b.family ?? null) &&
    a.dims.length === b.dims.length &&
    a.dims.every((dim, i) => dim === b.dims[i])
  );
}

/** A pinned question: a label and the card configuration it applies. */
export interface PinnedQuestion {
  id: string;
  label: string;
  card: Omit<PathwayCard, "id">;
}

const COMPOSITION_ID = "composition";
const SINGULARITY_ID = "singularite";

/**
 * Two cards, not seven. A filled grid is not an answer — and every card that can be added is one of
 * these two shapes, which is what makes the board learnable.
 */
function defaultCards(): PathwayCard[] {
  return [
    { id: COMPOSITION_ID, kind: "composition", dims: [], mode: "n", representation: null },
    { id: SINGULARITY_ID, kind: "singularite", dims: [], mode: "n", representation: null, family: null },
  ];
}

interface PathwayState {
  cards: PathwayCard[];
  conditions: PathwayConditions;
  pinned: PinnedQuestion[];
  railOpen: boolean;
  setRailOpen: (railOpen: boolean) => void;
  patchCard: (id: string, patch: Partial<PathwayCard>) => void;
  /** ``at: "start"`` puts it where the reader is looking — see the search bridge in PathwayBody. */
  addCard: (card: Omit<PathwayCard, "id">, at?: "start" | "end") => string;
  removeCard: (id: string) => void;
  resetCards: () => void;
  setConditions: (conditions: PathwayConditions) => void;
  toggleCondition: (dimension: string, value: string) => void;
  /** A range over the axis's raw value (PT-146); `null`, or one bounding nothing, clears the axis. */
  setRange: (dimension: string, range: PathwayRange | null) => void;
  clearConditions: () => void;
  pinQuestion: (question: Omit<PinnedQuestion, "id">) => void;
  unpinQuestion: (id: string) => void;
  /** Move a card. The order IS the arrangement, so this is the only write a drag performs. */
  moveCard: (from: number, to: number) => void;
  /** Adopt the server's arrangement, or keep the local one when the account has never saved. */
  hydrate: (layout: { cards?: PathwayCard[]; pinned?: PinnedQuestion[] } | null) => void;
  /** Back to a first visit: another account is signing in on this browser. */
  reset: () => void;
  /** Bumped by `reset`: the saved board has to be read again before anything is written over it. */
  epoch: number;
}

let sequence = 0;
const nextId = (prefix: string) => `${prefix}-${Date.now().toString(36)}-${(sequence += 1)}`;

/** The board's state and actions, whichever store holds them. */
function buildPathwayState(set: StoreApi<PathwayState>["setState"]): PathwayState {
  return {
    cards: defaultCards(),
    conditions: {},
    pinned: [],
    railOpen: true,
    setRailOpen: (railOpen) => set({ railOpen }),
    patchCard: (id, patch) =>
      set((state) => ({
        cards: state.cards.map((c) => (c.id === id ? { ...c, ...patch } : c)),
      })),
    // A question already on the board is not added twice: its card's id comes back instead.
    addCard: (card, at = "end") => {
      let id = nextId(card.kind);
      set((state) => {
        const existing = state.cards.find((c) => sameQuestion(c, card));
        if (existing) {
          id = existing.id;
          return state;
        }
        return {
          cards:
            at === "start"
              ? [{ ...card, id }, ...state.cards]
              : [...state.cards, { ...card, id }],
        };
      });
      return id;
    },
    // The two default cards are the board; only cards the reader added can be removed.
    removeCard: (id) =>
      set((state) => ({
        cards:
          id === COMPOSITION_ID || id === SINGULARITY_ID
            ? state.cards
            : state.cards.filter((c) => c.id !== id),
      })),
    resetCards: () => set({ cards: defaultCards() }),
    setConditions: (conditions) => set({ conditions }),
    toggleCondition: (dimension, value) =>
      set((state) => {
        // One condition per axis: ticking a bucket of an axis held by a range replaces the range.
        const current = labelsOf(state.conditions[dimension]);
        const next = current.includes(value)
          ? current.filter((v) => v !== value)
          : [...current, value];
        const conditions = { ...state.conditions };
        if (next.length === 0) delete conditions[dimension];
        else conditions[dimension] = next;
        return { conditions };
      }),
    setRange: (dimension, range) =>
      set((state) => {
        // And a range replaces the buckets ticked on its axis.
        const conditions = { ...state.conditions };
        const clean = cleanRange(range);
        if (clean) conditions[dimension] = clean;
        else delete conditions[dimension];
        return { conditions };
      }),
    clearConditions: () => set({ conditions: {} }),
    pinQuestion: (question) =>
      set((state) => ({ pinned: [...state.pinned, { ...question, id: nextId("q") }] })),
    unpinQuestion: (id) => set((state) => ({ pinned: state.pinned.filter((q) => q.id !== id) })),
    moveCard: (from, to) =>
      set((state) => {
        if (from === to || from < 0 || to < 0) return state;
        const cards = [...state.cards];
        if (from >= cards.length || to >= cards.length) return state;
        const [moved] = cards.splice(from, 1);
        cards.splice(to, 0, moved);
        return { cards };
      }),
    hydrate: (layout) => {
      // An account that never saved keeps whatever is on screen — hydrating it to the empty board
      // would wipe an arrangement the reader had just made and not yet saved.
      if (!layout || !Array.isArray(layout.cards) || layout.cards.length === 0) return;
      set({ cards: layout.cards, pinned: Array.isArray(layout.pinned) ? layout.pinned : [] });
    },
    reset: () =>
      set((state) => ({
        cards: defaultCards(),
        conditions: {},
        pinned: [],
        railOpen: true,
        epoch: state.epoch + 1,
      })),
    epoch: 0,
  };
}

/** The dashboard's board: persisted in the browser, and on the server through `useBoardSync`. */
export const dashboardPathwayStore = createStore<PathwayState>()(
  persist((set) => buildPathwayState(set), {
    name: "mage-x-dashboard-pathway",
    // v2: a condition may be a range (PT-146). Read unchanged here; the version is for the code before it,
    // which drops a board it cannot read instead of mapping over a range and crashing.
    version: 2,
    migrate: (persisted) => persisted as PathwayState,
  }),
);

/**
 * A board of its own, for a catalogue cohort consulted in a dialog (PT-96).
 *
 * Same shape, nothing shared: it starts from the two default cards, persists nothing and is never
 * synced, so a click inside the dialog cannot narrow the reader's dashboard, and the dashboard's
 * selection cannot narrow the cohort.
 */
export function createCohortPathwayStore(): StoreApi<PathwayState> {
  return createStore<PathwayState>()((set) => ({ ...buildPathwayState(set), railOpen: false }));
}

const PathwayStoreContext = createContext<StoreApi<PathwayState>>(dashboardPathwayStore);

/** Hands a board its store. A board without one is the dashboard's. */
export const PathwayStoreProvider = PathwayStoreContext.Provider;

/** The store of the board this component renders in. */
export function usePathwayStoreApi(): StoreApi<PathwayState> {
  return useContext(PathwayStoreContext);
}

export function usePathwayDashboard<T>(selector: (state: PathwayState) => T): T {
  return useStore(usePathwayStoreApi(), selector);
}

export { COMPOSITION_ID, SINGULARITY_ID };
